import React, { useState, useEffect } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import { Typography, Button } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Traffic = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#0f0f0f');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#1a1a1a');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [textColor, setTextColor] = useState(darkMode? '#FFFFFF' : '#000000');

  const [trafficSummaries, setTrafficSummaries] = useState({});

  const [totalTraffic, setTotalTraffic] = useState(34245);
  const [todayTraffic, setTodayTraffic] = useState(1632);
  const [totalSpeeding, setTotalSpeeding] = useState(75);
  const [todaySpeeding, setTodaySpeeding] = useState(31);
  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState('daily');
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [chartData, setChartData] = useState([]);
  const [packageAllowance, setPackageAllowance] = useState(false);

  const [deviceStatus] = useState({
    online: { count: 36, color: '#4CAF50' },
    powerSaving: { count: 7, color: '#FFC107' },
    offline: { count: 2, color: '#F44336' }
  });

  useEffect(() => {
    fetchPackageAllowance();
    fetchRecordSummaries();
  }, []);

  const fetchPackageAllowance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/package/log_access`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setPackageAllowance(data === 1);

    } catch (error) {
      console.error('Error fetching user package:', error);
      setError('Failed to fetch user package. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordSummaries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/record-summaries`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTrafficSummaries(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching presets:', error);
      setError('Failed to fetch presets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchChart = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/chart-data?timeRange=${timeRange}&device=${selectedDevice}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setChartData(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching traffic summaries:', error);
      setError('Failed to fetch traffic summaries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChart();
  }, [timeRange, selectedDevice]);


  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'primaryColor') {
        setPrimaryColor(event.newValue || '#0f0f0f');
      } else if (event.key === 'secondaryColor') {
        setSecondaryColor(event.newValue || '#1a1a1a');
      } else if (event.key === 'darkMode') {
        setDarkMode(event.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleColorChange = () => {
      setPrimaryColor(localStorage.getItem('primaryColor') || '#0f0f0f');
      setSecondaryColor(localStorage.getItem('secondaryColor') || '#1a1a1a');
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    };

    const intervalId = setInterval(handleColorChange, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  const CardSkeleton = () => (
    <div className="dashboard-card skeleton-card" style={{ backgroundColor: secondaryColor }}>
      <div className="dashboard-card-main">
        <div className="skeleton-title"></div>
        <div className="skeleton-value"></div>
        <div className="skeleton-text"></div>
      </div>
    </div>
  );

  const ChartSkeleton = () => (
    <div className="dashboard-card skeleton-chart" style={{ backgroundColor: secondaryColor }}>
      <div className="skeleton-title"></div>
      <div className="skeleton-chart-area"></div>
    </div>
  );

  const TableSkeleton = () => (
    <div className="dashboard-card skeleton-table" style={{ backgroundColor: secondaryColor }}>
      <div className="skeleton-title"></div>
      <div className="skeleton-rows">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton-row"></div>
        ))}
      </div>
    </div>
  );

  const getChartData = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    
    return {
      labels: timeRange === 'daily' 
          ? Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`)
          : Array.from({length: timeRange === 'weekly' ? 7 : 30}, (_, i) => {
              const date = new Date(yesterday);
              date.setDate(yesterday.getDate() - i);
              return timeRange === 'weekly'
                  ? date.toLocaleDateString('en-US', { weekday: 'short' })
                  : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }).reverse(),
      datasets: [
          {
              label: 'Last 7 Days Traffic',
              data: chartData.map(item => item.total),
              borderColor: '#4CAF50',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              tension: 0.4
          },
          {
              label: 'Speeding',
              data: chartData.map(item => item.speeding),
              borderColor: '#F44336',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              tension: 0.4
          }
      ]
    };
  };
  
  const trafficChartData = {
    labels: getChartData().labels,
    datasets: getChartData().datasets
  };
  /*
  const trafficChartData = {
    labels: getChartData().labels,
    datasets: [{
      label: 'Traffic',
      data: getChartData().data,
      borderColor: '#4CAF50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      hoverBackgroundColor: 'rgba(76, 175, 80, 0.3)',
      fill: true,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 10,
      segment: {
        borderColor: '#4CAF50',
      }
    }]
  };
  */
  
  const trafficChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: getChartData().title,
        color: darkMode ? '#CCCCCC' : '#666666',
        font: { size: 14 }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        titleColor: darkMode ? '#FFFFFF' : '#000000',
        bodyColor: darkMode ? '#FFFFFF' : '#000000',
        borderColor: '#4CAF50',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Records: ${context?.parsed?.y?.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: darkMode ? '#CCCCCC' : '#666666' }
      },
      y: {
        grid: { color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
        ticks: { color: darkMode ? '#CCCCCC' : '#666666' }
      }
    }
  };

  const screenStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: () => {
      navigate('/overview');
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: darkMode ? '#FFFFFF' : '#000000',
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        },
        onClick: () => null
      }
    },
    cutout: '50%',
    radius: '90%',
    hoverOffset: 10
  };
  
  const screenStatusData = {
    labels: ['Online', 'Power Saving', 'Offline'],
    datasets: [{
      data: [
        deviceStatus.online.count,
        deviceStatus.powerSaving.count,
        deviceStatus.offline.count
      ],
      backgroundColor: [
        deviceStatus.online.color,
        deviceStatus.powerSaving.color,
        deviceStatus.offline.color
      ],
      borderWidth: 0,
      cutout: '70%',
      radius: '90%'
    }]
  };
  

  const DashboardCard = ({ title, total, speeding }) => (
    <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
      <div style={{ backgroundColor: secondaryColor, padding: '20px', borderRadius: '8px' }}>
        <div style={{ marginBottom: '20px' }}>
          <Typography variant="h6">{title} Traffic Records</Typography>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Total</Typography>
              <Typography variant="h4">{total?.toLocaleString()}</Typography>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', color: '#F44336' }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Speeding</Typography>
              <Typography variant="h4">{speeding?.toLocaleString()}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ backgroundColor: primaryColor }}>
      {!packageAllowance ? (
              <div style={{ 
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: secondaryColor,
                color: textColor,
                padding: 0,
                textAlign: 'center',
                alignContent: 'center'
              }}>
                <div>
                  <Typography variant="h4" gutterBottom>
                    Traffic Analytics Access Restricted
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => navigate('/help')}
                    sx={{ 
                      mt: 2,
                      backgroundColor: primaryColor,
                      color: textColor,
                      '&:hover': {
                        backgroundColor: darkMode ? '#555' : '#e0e0e0',
                      }
                    }}
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
            ) : (
              <>
      <div className="dashboard-row">
        <div className="dashboard-column dashboard-card-container">
          {loading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              <DashboardCard title="Last 7 days" total={trafficSummaries.totalTrafficRecords} speeding={trafficSummaries.totalSpeedingRecords}/>
              <DashboardCard title="Yesterday" total={trafficSummaries.yesterdayTotalTrafficRecords} speeding={trafficSummaries.yesterdayTotalSpeedingRecords}/>
            </>
          )}
        </div>
        <div className="dashboard-column dashboard-chart-container">
          {loading ? <ChartSkeleton /> : <div className="dashboard-column dashboard-chart-container">
          <div className="dashboard-chart" style={{ backgroundColor: secondaryColor }}>
            <div className="chart-header">
              <h3 className="dashboard-card-title">Traffic Records</h3>
              <Select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 120,
                    color: 'inherit',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(76, 175, 80, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(76, 175, 80, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4CAF50',
                    },
                    '.MuiSelect-icon': {
                      color: 'inherit'
                    }
                  }}
                >
                  <MenuItem value="all">All devices</MenuItem>
                  {trafficSummaries.deviceSummaries?.map((device) => (
                    <MenuItem key={device.id} value={device.name}>{device.name}</MenuItem>
                  ))}
                </Select>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                size="small"
                sx={{
                  minWidth: 120,
                  color: 'inherit',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(76, 175, 80, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(76, 175, 80, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4CAF50',
                  },
                  '.MuiSelect-icon': {
                    color: 'inherit'
                  }
                }}
              >
                <MenuItem value="daily">Yesterday</MenuItem>
                <MenuItem value="weekly">Last 7 Days</MenuItem>
                <MenuItem value="monthly">Last 30 Days</MenuItem>
              </Select>
            </div>
            <div className="chart-container">
              <Line options={trafficChartOptions} data={trafficChartData} id="trafficChart" />
            </div>
          </div>
        </div>}
        </div>
      </div>

      <div className="dashboard-row">
        {loading ? (
          <>
            <TableSkeleton />
            <ChartSkeleton />
          </>
        ) : (
          <>
            <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Devices</h3>
              </div>
                <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Total Records</th>
                      <th>Total Speeding</th>
                      <th>Yesterday Records</th>
                      <th>Yesterday Speeding</th>
                      <th>Speed Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trafficSummaries?.deviceSummaries?.map((device) => (
                      <tr key={device.id}>
                        <td><b>{device.name}</b></td>
                        <td>{device.totalTrafficRecords}</td>
                        <td>{device.totalSpeedingRecords}</td>
                        <td>{device.yesterdayTotalTrafficRecords}</td>
                        <td>{device.yesterdayTotalSpeedingRecords}</td>
                        <td>{device.speedLimit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
              <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
                <div className="dashboard-card-header">
                    <h3 className="dashboard-card-title">Detailed Traffic Record</h3>
                  </div>
                  <div className="dashboard-card-content" style={{ padding: '20px' }}>
                    <Typography variant="body1" style={{ marginBottom: '20px' }}>
                      To see detailed traffic records, please login using your traffic data account.
                    </Typography>
                    
                  </div>
                  <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => navigate('/data-signin')}
                    >
                      Login
                    </Button>
              </div>
      
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default Traffic;